<template>
    <AuthLayout :loading="form.processing">
        <template #logo>
            <div class="flex justify-end items-center pb-8 ">
                <AppLogo />
            </div>
        </template>
        <div>
            <h2 class="text-2xl font-bold text-gray-800 mb-4">Create Account</h2>
            <p class="text-sm text-gray-600 mb-6">Join and explore amazing products.</p>

            <form @submit.prevent="submit">

                <!-- Role Selection -->
                <div class="mt-4">
                    <label class="block text-sm font-semibold text-gray-700">
                        I want to register as
                    </label>
                    <div class="flex gap-12 mt-2">
                        <label class="flex items-center">
                            <t-radio v-model="form.role" name="role" value="wholesaler" />
                            <span class="ml-2 text-sm">Wholesaler</span>
                        </label>
                        <label class="flex items-center">
                            <t-radio v-model="form.role" name="role" value="retailer" />
                            <span class="ml-2 text-sm">Retailer</span>
                        </label>
                    </div>
                </div>

                <!-- Category Selector for Wholesaler -->
                <transition name="fade-x">
                    <div v-if="form.role === 'wholesaler'" class="mt-4">
                        <label class="block text-sm font-semibold text-gray-700">
                            Category
                        </label>
                        <Multiselect v-model="selectedCategories" :options="categories" :multiple="true"
                            :close-on-select="false" placeholder="Pick category" label="name" track-by="name"
                            class="mt-2" />
                    </div>
                </transition>

                <!-- Business Name -->
                <div class="mt-4">
                    <JetLabel for="name" value="Business Name" />
                    <JetInput id="name" v-model="form.name" type="text" class="block w-full mt-1" required autofocus
                        autocomplete="name" />
                    <div v-if="errors('name')" class="text-red-500 text-sm mt-1">
                        {{ errors('name') }}
                    </div>
                </div>

                <!-- Email -->
                <div class="mt-4">
                    <JetLabel for="email" value="Email" />
                    <JetInput id="email" v-model="form.email" type="email" class="block w-full mt-1" required
                        autocomplete="email" />
                    <div v-if="errors('email')" class="text-red-500 text-sm mt-1">
                        {{ errors('email') }}
                    </div>
                </div>

                <!-- Passwords -->
                <div class="grid lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 gap-4 mt-4">
                    <div>
                        <JetLabel for="password" value="Password" />
                        <JetInput id="password" v-model="form.password" type="password" class="block w-full mt-1"
                            required autocomplete="new-password" />
                        <div v-if="errors('password')" class="text-red-500 text-sm mt-1">
                            {{ errors('password') }}
                        </div>
                    </div>
                    <div>
                        <JetLabel for="password_confirmation" value="Confirm Password" />
                        <JetInput id="password_confirmation" v-model="form.password_confirmation" type="password"
                            class="block w-full mt-1" required autocomplete="new-password" />
                    </div>
                </div>

                <!-- Terms and Privacy -->
                <div v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature" class="mt-4">
                    <JetLabel for="terms">
                        <div class="flex items-center">
                            <JetCheckbox id="terms" v-model="form.terms" name="terms" />
                            <div class="ml-2">
                                I agree to the
                                <a target="_blank" :href="route('terms.show')"
                                    class="text-sm text-gray-600 underline hover:text-gray-900">Terms of Service</a>
                                and
                                <a target="_blank" :href="route('policy.show')"
                                    class="text-sm text-gray-600 underline hover:text-gray-900">Privacy Policy</a>.
                            </div>
                        </div>
                    </JetLabel>
                </div>

                <!-- Submit Button + Redirect -->
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6">
                    <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Register
                    </JetButton>
                    <p class="text-sm text-gray-600">
                        Already have an account?
                        <inertia-link :href="route('login')" class="text-green-700 hover:text-green-500 font-semibold">
                            Login
                        </inertia-link>
                    </p>
                </div>
            </form>
        </div>
    </AuthLayout>
</template>


<script>
import AuthLayout from '@/Layouts/AuthLayout.vue';
import AppLogo from '@/Components/AppLogo.vue';
import Multiselect from 'vue-multiselect';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetLabel from '@/Jetstream/Label.vue';

export default {
    name: 'UserRegistration',

    components: {
        AuthLayout,
        AppLogo,
        JetButton,
        JetInput,
        JetCheckbox,
        JetLabel,
        Multiselect,
    },

    data() {
        return {
            selectedCategories: [],
            form: this.$inertia.form({
                name: '',
                email: '',
                role: 'wholesaler',
                categoryIds: [],
                password: '',
                password_confirmation: '',
                terms: false,
            }),
            categories: [],

            // steps: 3,
            // step: 1,
        };
    },

    watch: {
        selectedCategories: {
            deep: true,
            handler() {
                this.form.categoryIds = this.selectedCategories.map((category) => category.id);
            },
        },
    },

    created() {
        this.$axios.get(route('categories.parent')).then((res) => {
            this.categories = res.data.categories;
        });
    },

    methods: {
        // next() {
        //     if (this.step < this.steps) this.step += 1;
        // },

        // previous() {
        //     if (this.step > 1) this.step -= 1;
        // },

        submit() {
            this.form.post(this.route('register'), {
                onFinish: () => this.form.reset('password', 'password_confirmation'),
            });
        },
    },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.fade-x-enter-active,
.fade-x-leave-active {
    transition: opacity 0.3s ease;
}

.fade-x-enter,
.fade-x-leave-to {
    opacity: 0;
}
</style>
